import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PhygitalIcon from "@/components/PhygitalIcon";
import SharedFooter from "@/components/SharedFooter";
import {
  Sparkles,
  Bell,
  Building,
  ArrowRight,
  Check,
  Users,
  Calendar,
  Image,
  MessageSquare,
  BarChart3,
  Settings,
  Share,
  Eye,
  Palette,
  Scissors,
  Heart,
  PartyPopper,
  Leaf,
  ChevronRight
} from "lucide-react";

// Rotating words for hero - hair, nail, makeup, event, wellness
// First word on load: "hair", 2500ms display, 400ms crossfade opacity only
const ROTATING_WORDS = ["hair", "nail", "makeup", "event", "wellness"];

// Pilot type configurations
const PILOT_CONFIGS = {
  // Beauty service providers - full access
  hair: {
    title: "Hair",
    category: "beauty",
    showHome: true,
    showDemos: true,
    showDashboard: true,
    icon: Scissors,
    color: "bg-pink-100 text-pink-600"
  },
  nail: {
    title: "Nail",
    category: "beauty",
    showHome: true,
    showDemos: true,
    showDashboard: true,
    icon: Palette,
    color: "bg-rose-100 text-rose-600"
  },
  makeup: {
    title: "Makeup",
    category: "beauty",
    showHome: true,
    showDemos: true,
    showDashboard: true,
    icon: Sparkles,
    color: "bg-purple-100 text-purple-600"
  },
  wellness: {
    title: "Wellness",
    category: "beauty",
    showHome: true,
    showDemos: true,
    showDashboard: true,
    icon: Leaf,
    color: "bg-emerald-100 text-emerald-600"
  },
  // Event pilots - limited access (Coming Soon + Confirmation/Host pages only)
  event: {
    title: "Event",
    category: "event",
    showHome: false,
    showDemos: false,
    showDashboard: true,
    icon: PartyPopper,
    color: "bg-amber-100 text-amber-600"
  },
  // Instructor pilots - limited access
  instructor: {
    title: "Instructor",
    category: "instructor",
    showHome: false,
    showDemos: false,
    showDashboard: true,
    icon: Users,
    color: "bg-blue-100 text-blue-600"
  }
};

// Feature cards for beauty pilots (existing deployed features)
const BEAUTY_FEATURES = [
  {
    icon: Users,
    title: "Client Management",
    description: "Track preferences, history, and notes for every client"
  },
  {
    icon: Calendar,
    title: "Smart Scheduling",
    description: "Effortless booking with automated reminders"
  },
  {
    icon: Image,
    title: "Visual Portfolio",
    description: "Showcase your work with beautiful galleries"
  },
  {
    icon: MessageSquare,
    title: "Client Communication",
    description: "Direct messaging with preference sharing"
  },
  {
    icon: Share,
    title: "Preference Cards",
    description: "Shareable cards that clients can send to any provider"
  },
  {
    icon: BarChart3,
    title: "Business Insights",
    description: "Track your growth and client engagement"
  }
];

// Event pilot features (only show existing deployed features)
const EVENT_FEATURES = [
  {
    icon: Users,
    title: "Guest Management",
    description: "VIP lists, RSVPs, and real-time check-in"
  },
  {
    icon: Calendar,
    title: "Event Dashboard",
    description: "Complete control over your event details"
  },
  {
    icon: Share,
    title: "Invite Links",
    description: "Custom invite pages for your guests"
  },
  {
    icon: Bell,
    title: "Notifications",
    description: "Automated confirmations via Beehiiv"
  }
];

export default function PilotPortalPage() {
  const { pilotType } = useParams();
  const navigate = useNavigate();
  const [currentWordIndex, setCurrentWordIndex] = useState(0); // Start with "hair"
  const [isAnimating, setIsAnimating] = useState(false);
  const [user, setUser] = useState(null);

  const config = PILOT_CONFIGS[pilotType?.toLowerCase()] || PILOT_CONFIGS.hair;
  const isBeautyPilot = config.category === "beauty";
  const features = isBeautyPilot ? BEAUTY_FEATURES : EVENT_FEATURES;

  // Get user info if logged in
  useEffect(() => {
    const token = localStorage.getItem("session_token");
    if (token) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => setUser(data))
        .catch(() => setUser(null));
    }
  }, []);

  // Rotating words animation - 2500ms display, 400ms crossfade
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentWordIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
        setIsAnimating(false);
      }, 400); // 400ms crossfade
    }, 2500); // 2500ms display

    return () => clearInterval(interval);
  }, []);

  const currentWord = ROTATING_WORDS[currentWordIndex];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Universal across every pilot portal */}
      <section className="relative overflow-hidden">
        {/* Fixed: white background */}
        <div className="absolute inset-0 bg-white" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-8">
          {/* Nav - Name and Company and Bell top right, tab order */}
          <nav className="flex items-center justify-between mb-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <PhygitalIcon className="w-9 h-9 sm:w-10 sm:h-10" variant="light" />
              <span className="font-semibold text-xl sm:text-2xl text-black">phygital</span>
            </div>
            <div className="flex items-center gap-4">
              {user ? (
                <>
                  <div className="hidden sm:flex items-center gap-2 text-sm text-gray-600">
                    <span>{user.name || user.email?.split("@")[0]}</span>
                    {user.company && (
                      <>
                        <span className="text-gray-300">|</span>
                        <span className="flex items-center gap-1">
                          <Building className="w-3.5 h-3.5" />
                          {user.company}
                        </span>
                      </>
                    )}
                  </div>
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <Bell className="w-5 h-5 text-gray-600" />
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="text-sm text-gray-600 hover:text-gray-900 font-medium"
                >
                  Sign In
                </Link>
              )}
            </div>
          </nav>

          {/* Hero Content - Centered */}
          <div className="text-center max-w-3xl mx-auto">
            {/* Sparkles icon */}
            <div className="flex justify-center mb-6">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-gray-700" />
              </div>
            </div>

            {/* Main heading with rotating word */}
            {/* Format: [rotating word] preferences, perfectly communicated. */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black mb-6 tracking-tight">
              <span
                className={`inline-block transition-opacity duration-[400ms] ${
                  isAnimating ? "opacity-0" : "opacity-100"
                }`}
              >
                {currentWord}
              </span>{" "}
              preferences,
              <br />
              perfectly communicated.
            </h1>

            {/* Subheading */}
            <p className="text-base sm:text-lg text-gray-600 mb-10 max-w-xl mx-auto">
              {isBeautyPilot
                ? "The platform that helps beauty professionals and their clients communicate preferences seamlessly."
                : "Streamlined event management with VIP lists, RSVPs, and real-time guest check-in."}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {config.showHome && (
                <Button
                  onClick={() => navigate("/home")}
                  className="bg-black text-white hover:bg-gray-800 h-12 px-8 rounded-full font-medium text-base"
                  data-testid="pilot-explore-btn"
                >
                  Explore Platform
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              {config.showDemos && (
                <Button
                  onClick={() => navigate(`/${pilotType?.toLowerCase() || "nail"}`)}
                  variant="outline"
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 h-12 px-8 rounded-full font-medium text-base"
                  data-testid="pilot-demo-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Demo
                </Button>
              )}
              {!config.showHome && config.showDashboard && (
                <Button
                  onClick={() => navigate("/me")}
                  className="bg-black text-white hover:bg-gray-800 h-12 px-8 rounded-full font-medium text-base"
                  data-testid="pilot-dashboard-btn"
                >
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* User Flow Visual Section - Simple illustration style */}
      <section className="py-16 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-black mb-4">
            How it works
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            {isBeautyPilot
              ? "A seamless experience for both providers and clients"
              : "Simple and powerful event management"}
          </p>

          {/* Simple 3-step flow */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {isBeautyPilot ? (
              <>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-gray-400">1</span>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Create Profile</h3>
                  <p className="text-gray-600 text-sm">
                    Set up your provider profile with your specialties and portfolio
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-gray-400">2</span>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Share & Connect</h3>
                  <p className="text-gray-600 text-sm">
                    Clients share preference cards with you before appointments
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-gray-400">3</span>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Deliver Excellence</h3>
                  <p className="text-gray-600 text-sm">
                    Every appointment starts with full context of client preferences
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-gray-400">1</span>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Create Event</h3>
                  <p className="text-gray-600 text-sm">
                    Set up your event with all the details and VIP lists
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-gray-400">2</span>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Send Invites</h3>
                  <p className="text-gray-600 text-sm">
                    Share personalized invite links with your guests
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-gray-400">3</span>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Manage Check-in</h3>
                  <p className="text-gray-600 text-sm">
                    Real-time guest check-in with QR codes and Apple Wallet
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Features Grid - Simple display */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-black mb-4">
            {isBeautyPilot ? "Built for beauty professionals" : "Event management features"}
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            {isBeautyPilot
              ? "Everything you need to run your business and delight your clients"
              : "All the tools you need for successful events"}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="p-6 bg-white border border-gray-100 rounded-xl hover:border-gray-200 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center mb-4">
                  <feature.icon className="w-5 h-5 text-gray-700" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 bg-black text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Ready to get started?
          </h2>
          <p className="text-gray-400 mb-8">
            {isBeautyPilot
              ? "Join the pilot program and transform how you connect with clients"
              : "Start managing your events with powerful tools"}
          </p>
          <Button
            onClick={() => navigate(config.showHome ? "/home" : "/me")}
            className="bg-white text-black hover:bg-gray-100 h-12 px-8 rounded-full font-medium text-base"
            data-testid="pilot-cta-btn"
          >
            {config.showHome ? "Explore Platform" : "Go to Dashboard"}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer Disclaimer - Fixed */}
      <footer className="py-8 px-4 sm:px-6 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs text-gray-500">
            This is a private pilot program. Access is by invitation only.
            <br />
            © 2026 Phygital Systems. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
